import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { formatDate } from "../../utils/dateUtils";
import { prefetchBlogPost } from "../../utils/blogUtils";
import { useState } from "react";

const BlogCard = ({ post, index, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <Link
      to={`/blog/${post.slug}`}
      onClick={onClick}
      onMouseEnter={() => prefetchBlogPost(post.slug)}
      onFocus={() => prefetchBlogPost(post.slug)}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-xl group"
      aria-label={`Read article: ${post.title}`}
    >
      <motion.article
        id={`blog-card-${post.slug}`}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.08, ease: "easeOut" }}
        whileHover={{ y: -4 }}
        className="h-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 flex flex-col justify-between"
      >
        {/* Cover Image */}
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-gray-700">
          {/* Category badge */}
          <div className="absolute top-3 left-3 z-10">
            <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-600/90 dark:bg-blue-500/90 text-white backdrop-blur-sm shadow-sm">
              {post.category}
            </span>
          </div>

          {/* Image skeleton */}
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 skeleton-shimmer" />
          )}

          {/* Fallback gradient when no image */}
          {imageError ? (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-indigo-500/20 dark:from-blue-600/30 dark:via-purple-600/30 dark:to-indigo-600/30 flex items-center justify-center">
              <span className="text-4xl opacity-50">📝</span>
            </div>
          ) : (
            <img
              src={post.coverImage}
              alt={post.title}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
            />
          )}

          {/* Subtle overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col justify-between">
          <div>
            {/* Title */}
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 font-display">
              {post.title}
            </h3>

            {/* Excerpt */}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 leading-relaxed">
              {post.excerpt}
            </p>
          </div>

          <div>
            {/* Meta row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar size={13} />
                  {formatDate(post.date)}
                </span>
                <span className="inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                <span className="flex items-center gap-1">
                  <Clock size={13} />
                  {post.readingTime || "3 min read"}
                </span>
              </div>
              <span className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Read
                <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              {post.tags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border border-gray-200/60 dark:border-gray-600/40"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.article>
    </Link>
  );
};

export default BlogCard;
